// Header.tsx - Barra superior del panel (diseño ITOBOX Platform 2.0)
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, LogOut, Settings, Menu } from 'lucide-react';
import { useAuth } from '../shared/hooks/useAuth';
import { useTenant } from '../contexts/TenantContext';
import { signOut } from 'firebase/auth';
import { auth } from '../shared/config/firebase';

interface HeaderProps {
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { tenant } = useTenant();
  const [search, setSearch] = useState('');
  const name = userData?.name || userData?.email?.split('@')[0] || 'Usuario';
  const initials = name.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  // Antes el buscador no hacía nada: ahora busca en Paquetes (tracking, descripción o cliente)
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = search.trim();
    navigate(q ? `/packages?q=${encodeURIComponent(q)}` : '/packages');
  };

  return (
    <header className="fixed top-0 left-0 lg:left-[260px] right-0 h-16 bg-white border-b border-[#EEF0F3] z-[999]"
      style={{ fontFamily: 'Poppins, system-ui, sans-serif' }}>
      <div className="flex items-center justify-between h-full px-3 sm:px-6 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={onMenuClick}
            aria-label="Abrir menú"
            className="lg:hidden p-2 -ml-1 text-[#374151] hover:bg-[#F5F6F8] rounded-xl flex-shrink-0"
          >
            <Menu size={22} />
          </button>
          <h1 className="m-0 text-base font-semibold text-[#111111] truncate lg:hidden">
            {tenant?.branding?.company_name || 'Sistema Courier'}
          </h1>
        </div>

        <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl mx-4">
          <div className="relative w-full">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={18} />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar paquete por tracking, descripción o cliente"
              aria-label="Buscar paquetes"
              className="w-full h-11 pl-10 pr-3 rounded-xl bg-[#F5F6F8] border border-transparent text-sm outline-none placeholder:text-[#9CA3AF] focus:bg-white focus:border-[#E5E7EB]"
            />
          </div>
        </form>

        <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
          <button
            className="hidden sm:inline-flex p-2 text-[#6B7280] hover:bg-[#F5F6F8] rounded-xl"
            aria-label="Personalización del sistema"
            title="Personalización del sistema"
            onClick={() => navigate('/account/branding')}
          >
            <Settings size={20} />
          </button>

          <div className="hidden sm:flex items-center gap-2.5 pl-3 border-l border-[#EEF0F3]">
            <div className="w-9 h-9 rounded-full bg-[#F6C27A] flex items-center justify-center font-bold text-sm text-[#111111]">
              {initials}
            </div>
            <div className="flex-col items-start hidden md:flex">
              <span className="text-xs font-semibold text-[#111111]">{name}</span>
              <span className="text-[11px] text-[#6B7280] capitalize">{userData?.role || ''}</span>
            </div>
          </div>

          <button
            className="p-2 text-[#9CA3AF] hover:bg-[#FDE8E9] hover:text-[#E50914] rounded-xl"
            onClick={handleLogout}
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
